import React from 'react';
import { TargetIcon, EyeIcon, HeartIcon, ZapIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
export function AboutPage() {
  usePageSEO({
    title: 'About | Design Flow Digitals',
    description: 'Learn about Design Flow Digitals — a Shopify-focused development company building premium ecommerce experiences with custom themes, apps, and conversion-optimized stores.'
  });
  const philosophies = [{
    title: 'Simple',
    icon: <HeartIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-356" />
  }, {
    title: 'Fast',
    icon: <ZapIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-357" />
  }, {
    title: 'Beautiful',
    icon: <EyeIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-358" />
  }, {
    title: 'Effective',
    icon: <TargetIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-359" />
  }];
  return <div className="pt-32 pb-20" data-id="element-360">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-361">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-362">
          About Design Flow Digitals
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-363">
          Design Flow Digitals is a Shopify development company focused on
          building premium ecommerce experiences.
        </p>
      </section>

      {/* Specializations & Goal */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-364">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-365">
          <div data-id="element-366">
            <h2 className="text-3xl font-bold mb-8" data-id="element-367">We specialize in:</h2>
            <ul className="space-y-4" data-id="element-368">
              {['Shopify theme development', 'Shopify app development', 'Custom Shopify store design', 'Performance optimization'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-gray-300" data-id="element-369">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-370" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="glass-card p-10 border-[#D4AF37]/20 relative overflow-hidden" data-id="element-371">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" data-id="element-372" />
            <h2 className="text-2xl font-bold mb-4 text-white" data-id="element-373">
              Our goal is simple:
            </h2>
            <p className="text-2xl text-gradient-gold font-medium leading-relaxed" data-id="element-374">
              Create ecommerce products and experiences that help businesses
              grow faster.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding" data-id="element-375">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-376">
          <h2 className="text-4xl font-bold mb-6" data-id="element-377">Our Philosophy</h2>
          <p className="text-xl text-gray-400" data-id="element-378">
            Great ecommerce design should be:
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-379">
          {philosophies.map((phil, i) => <div key={i} className="glass-card p-8 text-center flex flex-col items-center gap-4" data-id="element-380">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-381">
                {phil.icon}
              </div>
              <h3 className="text-xl font-bold text-white" data-id="element-382">{phil.title}</h3>
            </div>)}
        </div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-383">
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-384">
            Inspired by the product design principles used by companies like
            Apple, we believe that simplicity and performance create the best
            user experiences.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-black" data-id="element-385">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-386">
          <div className="glass-card p-12" data-id="element-387">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-388">
              Our Mission
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-389">
              To help modern ecommerce brands launch and scale with powerful
              Shopify technology.
            </p>
          </div>
          <div className="glass-card p-12" data-id="element-390">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-391">
              Our Vision
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-392">
              To become one of the most trusted creators of Shopify themes,
              apps, and ecommerce experiences globally.
            </p>
          </div>
        </div>
      </section>

      {/* Company Details */}
      <section className="section-padding text-center" data-id="element-393">
        <div className="inline-block glass-card p-10 border-white/10" data-id="element-394">
          <h2 className="text-2xl font-bold mb-6" data-id="element-395">Company Details</h2>
          <div className="text-gray-400 space-y-2" data-id="element-396">
            <p className="font-medium text-white" data-id="element-397">Design Flow Digitals</p>
            <p data-id="element-398">Part of Pankaj Maurya LLC</p>
            <p data-id="element-399">67 Wigley Loop</p>
            <p data-id="element-400">Whitwell, TN, 37397</p>
            <p data-id="element-401">United States</p>
            <p className="mt-4 pt-4 border-t border-white/10" data-id="element-402">
              Support:{' '}
              <a href="mailto:support@designflowdigitals.com" className="text-[#D4AF37] hover:underline" data-id="element-403">
                support@designflowdigitals.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>;
}